import { use, useEffect, useMemo, useState } from 'react';
import { dateRangeOverlapsDate, thisMonthDate, todayDate } from '../../../../../../common/src/date_string.ts';
import { Hymn, MessageType, SongFileExtension, SongListType } from '../../../../../../common/src/model.ts';
import { getSongListDateRange, getWeeklySongListType, isBound } from '../../../../../../common/src/model_util.ts';
import { getWeeklyDateForOrganization } from '../../../../../../common/src/org.ts';
import { LocalStorageKey } from '../../../../data/client_local_storage.ts';
import { getSongLists, synchronizeSchoolTerms } from '../../../../data/use_current_org.tsx';
import { useCustomMusic } from '../../../../data/use_custom_music.tsx';
import { HymnalsContext } from '../../../../data/use_hymnals.tsx';
import { useLocalStorage } from '../../../../data/use_local_storage.tsx';
import { useAllOrgs } from '../../../../data/use_orgs.tsx';
import { refreshMusicCache } from '../../../../util/music_cache_refresh.ts';
import { getHymnFromSlug } from '../../../../util/path.ts';
import { HymnsByOrg } from './engineering_song_list.tsx';

export function useBrokenSongsForThisSunday(): { brokenSongsByOrg: HymnsByOrg; sundaysPopulated: boolean } {
  const [brokenSongsByOrg, setBrokenSongsByOrg] = useState<HymnsByOrg>({});
  const [sundaysPopulated, setSundaysPopulated] = useState(false);
  const { customMusic, setIncludeAllOrgs, clearIncludeAllOrgs } = useCustomMusic();
  const hymnals = use(HymnalsContext);
  const { orgs } = useAllOrgs();
  const [, setHymnalsHash] = useLocalStorage(LocalStorageKey.HymnalsHash);

  useEffect(() => {
    setHymnalsHash(undefined); // redownload all songs to get latest versions
  }, [setHymnalsHash]);

  useEffect(() => {
    setIncludeAllOrgs();
    return () => clearIncludeAllOrgs();
  }, [setIncludeAllOrgs, clearIncludeAllOrgs]);

  useEffect(() => {
    if (orgs.length === 0) {
      return;
    }
    void (async () => {
      let brokenHymnsByOrg: HymnsByOrg = {}; // can't depend on state updates
      const today = todayDate();
      for (const org of orgs) {
        await synchronizeSchoolTerms(org);
        const songLists = await getSongLists(org);
        const weeklyDate = getWeeklyDateForOrganization(org, today);
        const monthlyDate = thisMonthDate(today);
        for (const list of songLists) {
          const { type, songs } = list;
          const dateRange = getSongListDateRange(list);
          if (
            (type === getWeeklySongListType(org) && dateRangeOverlapsDate(dateRange, weeklyDate)) ||
            (type === SongListType.HymnOfTheMonth && list.date === monthlyDate) ||
            type === SongListType.Event
          ) {
            for (const songListEntry of songs) {
              if (!isBound(songListEntry)) {
                continue;
              }
              const hymn = getHymnFromSlug({ songSlug: songListEntry.slug, hymnals, customMusic });
              if (!hymn) {
                continue;
              }
              brokenHymnsByOrg = addHymnIfBroken({
                hymn,
                orgId: org.id,
                brokenHymnsByOrg,
              });
            }
          }
        }
      }
      setBrokenSongsByOrg(brokenHymnsByOrg);
      setSundaysPopulated(true);
    })();
  }, [orgs, hymnals, customMusic]);
  return { brokenSongsByOrg, sundaysPopulated };
}

export function useBrokenCustomMusic(): { brokenCustomMusicByOrg: HymnsByOrg } {
  const { customMusic, setIncludeAllOrgs, clearIncludeAllOrgs } = useCustomMusic();
  const { orgs } = useAllOrgs();

  useEffect(() => {
    setIncludeAllOrgs();
    return () => clearIncludeAllOrgs();
  }, [setIncludeAllOrgs, clearIncludeAllOrgs]);

  const brokenCustomMusicByOrg = useMemo(() => {
    if (orgs.length === 0) {
      return {};
    }
    let brokenHymnsByOrg = {}; // can't depend on state updates
    for (const org of orgs) {
      const customHymns = customMusic[org.id];
      for (const hymn of customHymns ?? []) {
        brokenHymnsByOrg = addHymnIfBroken({
          hymn,
          orgId: org.id,
          brokenHymnsByOrg,
          checkForXmlFile: true,
        });
      }
    }
    return brokenHymnsByOrg;
  }, [orgs, customMusic]);

  return { brokenCustomMusicByOrg };
}

function addHymnIfBroken({
  hymn,
  orgId,
  brokenHymnsByOrg,
  checkForXmlFile,
}: {
  hymn: Hymn;
  orgId: number;
  brokenHymnsByOrg: HymnsByOrg;
  checkForXmlFile?: boolean;
}) {
  if (
    !hymn.isXmlEmpty &&
    (!hymn.extensions?.includes(SongFileExtension.Mid) || hymn.messageCounts?.[MessageType.Error])
  ) {
    if (checkForXmlFile) {
      if (!hymn.extensions?.includes(SongFileExtension.Xml)) {
        return brokenHymnsByOrg; // song is not considered broken if we haven't yet uploaded a MusicXML file from SoundSlice
      }
    }
    const hymns = brokenHymnsByOrg[orgId] ?? [];
    const brokenSongsForOrg = [...hymns, hymn];
    brokenHymnsByOrg = { ...brokenHymnsByOrg, [orgId]: brokenSongsForOrg };
  }
  return brokenHymnsByOrg;
}

export function useForceRefreshMusicCaches() {
  const [, setCustomMusicHash] = useLocalStorage(LocalStorageKey.CustomMusicHash);
  const [, setHymnalsHash] = useLocalStorage(LocalStorageKey.HymnalsHash);
  useEffect(() => {
    // re-download all songs to get latest versions
    setHymnalsHash(undefined);
    setCustomMusicHash(undefined);
    void refreshMusicCache({ trigger: 'explicit' });
  }, [setCustomMusicHash, setHymnalsHash]);
}
